import React, { type FC, type PropsWithChildren, useLayoutEffect } from 'react'
import { HashRouter } from 'react-router-dom'
import { CssBaseline } from '@mui/material'

import { Head } from '..'
import Certificates from '../Certificates'
import Connections, { Details as ConnectionDetails } from '../Connections'
import { ServiceContainerDetails, ServiceContainersTable } from '../Containers'
import Credentials from '../Credentials'
import DeniedAccess from '../DeniedAccess'
import DeviationNotifications from '../DeviationNotifications'
import Endpoints, { Details as EndpointDetails } from '../Endpoints'
import Explorer from '../Explorer'
import { JSONEditor } from '../JsonEditor'
import Mappings, { Details as MappingDetails } from '../Mappings'
import { AppNavigation, RouteNavigation } from '../Navigation'
import Nodes from '../Nodes'
import ResourcesTabs from '../Resources'
import {
    AbsoluteRouteOnlyPath,
    AbsoluteRoutePathWithId,
    AbsoluteRoutePathWithServiceAndResourceId,
    AbsoluteRoutePathWithServiceId,
    CybusRouter,
    Redirect,
    Route,
    SwitchingRoute,
} from '../routing'
import RuleEngineSandbox from '../RuleEngine'
import Servers, { Details as ServerDetails } from '../Servers'
import ServiceLogs from '../ServiceLogs'
import Services, { DeviatedServices, Details as ServiceDetails, ServiceLinks, ServicesCatalog } from '../Services'
import { LoginSettings } from '../Settings'
import { useAppUsecases } from '../State'
import {
    InformationPage,
    SystemAgents,
    SystemContainerDetails,
    SystemContainers,
    SystemInternetConnectivity,
    SystemLicense,
    SystemMetrics,
    SystemStatusTabs,
} from '../SystemStatus'
import { ClientRegistry, PermissionsPage, RolesPage, UserManagementTabs, UsersPage } from '../Users'
import VersionMismatchNotifier from '../VersionMismatch'
import Volumes, { Details as VolumeDetails } from '../Volumes'
import Workbench from '../Workbench'

const RouterAppNavigation: FC<PropsWithChildren> = ({ children }) => (
    <>
        <CssBaseline />
        <Credentials>
            <AppNavigation>{children}</AppNavigation>
            <DeviationNotifications />
        </Credentials>
    </>
)

export const App: FC = () => {
    const { loginFromTokenUsecase, loginFromPersistenceUsecase, debugApplicationUsecase } = useAppUsecases()

    useLayoutEffect(() => {
        /**
         * Attempt to login from either the token on the url
         * or the persisted information from local storage
         */
        loginFromTokenUsecase.loginBrowserFromToken() || loginFromPersistenceUsecase.loginWithPersistedInformation()

        /**
         * Activate debugging of the application
         */
        debugApplicationUsecase.activate()
    }, [loginFromTokenUsecase, loginFromPersistenceUsecase, debugApplicationUsecase])

    return (
        <>
            <Head />
            <HashRouter>
                <CybusRouter
                    initial={AbsoluteRouteOnlyPath.SYSTEM}
                    onNotFound={AbsoluteRouteOnlyPath.SYSTEM}
                    appNavigation={RouterAppNavigation}
                    routeNavigation={RouteNavigation}
                    deniedAccess={DeniedAccess}
                >
                    {/* System */}

                    <Route path={AbsoluteRouteOnlyPath.SYSTEM}>
                        <Redirect to={AbsoluteRouteOnlyPath.SYSTEM_STATUS} />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SYSTEM_STATUS}>
                        <Redirect to={AbsoluteRouteOnlyPath.SYSTEM_LICENSE} fallbackToSiblings />
                    </Route>
                    <SwitchingRoute prefix={SystemStatusTabs}>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_CONTAINERS}>
                            <SystemContainers />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_INTERNET_CONNECTIVITY}>
                            <SystemInternetConnectivity />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_METRICS}>
                            <SystemMetrics />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_AGENTS}>
                            <SystemAgents />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_LICENSE}>
                            <SystemLicense />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SYSTEM_INFORMATION}>
                            <InformationPage />
                        </Route>
                    </SwitchingRoute>
                    <Route path={AbsoluteRoutePathWithId.SYSTEM_CONTAINER}>
                        <SystemContainerDetails />
                    </Route>

                    {/* Data */}

                    <Route path={AbsoluteRouteOnlyPath.DATA}>
                        <Redirect to={AbsoluteRouteOnlyPath.DATA_EXPLORER} />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.DATA_EXPLORER}>
                        <Explorer />
                    </Route>

                    {/* Services */}

                    <Route path={AbsoluteRouteOnlyPath.SERVICES}>
                        <Redirect to={AbsoluteRouteOnlyPath.SERVICES_OVERVIEW} fallbackToSiblings />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_OVERVIEW}>
                        <Services />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.DEVIATED_SERVICES}>
                        <DeviatedServices />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_SERVER, AbsoluteRoutePathWithId.SERVICES_RESOURCES_SERVER]}>
                        <ServerDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_CONTAINER, AbsoluteRoutePathWithId.SERVICES_RESOURCES_CONTAINER]}>
                        <ServiceContainerDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_VOLUME, AbsoluteRoutePathWithId.SERVICES_RESOURCES_VOLUME]}>
                        <VolumeDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_CONNECTION, AbsoluteRoutePathWithId.SERVICES_RESOURCES_CONNECTION]}>
                        <ConnectionDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_ENDPOINT, AbsoluteRoutePathWithId.SERVICES_RESOURCES_ENDPOINT]}>
                        <EndpointDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceAndResourceId.SERVICE_MAPPING, AbsoluteRoutePathWithId.SERVICES_RESOURCES_MAPPING]}>
                        <MappingDetails />
                    </Route>
                    <Route paths={[AbsoluteRoutePathWithServiceId.SERVICES_OVERVIEW_SERVICE, AbsoluteRoutePathWithId.SERVICES_RESOURCES_SERVICE]}>
                        <ServiceDetails />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_LOGS}>
                        <ServiceLogs />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES}>
                        <Redirect to={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_LINKS} fallbackToSiblings />
                    </Route>
                    <SwitchingRoute prefix={ResourcesTabs}>
                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_LINKS}>
                            <ServiceLinks />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_SERVERS}>
                            <Servers />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_NODES}>
                            <Nodes />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_CONTAINERS}>
                            <ServiceContainersTable />
                        </Route>

                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_VOLUMES}>
                            <Volumes />
                        </Route>

                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_CONNECTIONS}>
                            <Connections />
                        </Route>

                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_ENDPOINTS}>
                            <Endpoints />
                        </Route>

                        <Route path={AbsoluteRouteOnlyPath.SERVICES_RESOURCES_MAPPINGS}>
                            <Mappings />
                        </Route>
                    </SwitchingRoute>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_CATALOG}>
                        <ServicesCatalog />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_RULE_ENGINE_SANDBOX}>
                        <RuleEngineSandbox Editor={JSONEditor} />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SERVICES_WORKBENCH}>
                        <Workbench />
                    </Route>

                    {/* User */}

                    <Route path={AbsoluteRouteOnlyPath.USER}>
                        <Redirect to={AbsoluteRouteOnlyPath.USER_MANAGEMENT} />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.USER_MANAGEMENT}>
                        <Redirect to={AbsoluteRouteOnlyPath.USER_MANAGEMENT_USERS} fallbackToSiblings />
                    </Route>
                    <SwitchingRoute prefix={UserManagementTabs}>
                        <Route path={AbsoluteRouteOnlyPath.USER_MANAGEMENT_USERS}>
                            <UsersPage />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.USER_MANAGEMENT_ROLES}>
                            <RolesPage />
                        </Route>
                        <Route path={AbsoluteRouteOnlyPath.USER_MANAGEMENT_PERMISSIONS}>
                            <PermissionsPage />
                        </Route>
                    </SwitchingRoute>
                    <Route path={AbsoluteRouteOnlyPath.USER_CLIENT_REGISTRY}>
                        <ClientRegistry />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.USER_CERTIFICATES}>
                        <Certificates />
                    </Route>

                    {/* Settings */}

                    <Route path={AbsoluteRouteOnlyPath.SETTINGS}>
                        <Redirect to={AbsoluteRouteOnlyPath.SETTINGS_LOGIN} />
                    </Route>
                    <Route path={AbsoluteRouteOnlyPath.SETTINGS_LOGIN}>
                        <LoginSettings />
                    </Route>
                </CybusRouter>
            </HashRouter>
            <VersionMismatchNotifier />
        </>
    )
}
